import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Switch, IconButton, Button, Tooltip, Zoom, Chip, useTheme,
  Dialog, DialogTitle, DialogActions,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DeleteIcon from '@mui/icons-material/Delete';
import { useApp } from '../context/AppContext';

const ENV_COLORS: Record<string, string> = {
  dev: '#00C7BE',
  staging: '#FF9F0A',
  prod: '#FF3B30',
};

function envColor(key: string): string {
  return ENV_COLORS[key] ?? '#8E8E93';
}

const DEFAULT_KEYS = new Set(['app-enable', 'app-maintenance-mode']);

export default function Dashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { projects, flags, currentProject, loading, loadProjects, loadFlags, selectProject, updateFlag, deleteFlag } = useApp();

  // suppress unused variable warning
  void projects;
  void loading;

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const borderColor = isDark ? '#2C2C2E' : '#E5E5EA';
  const keyBg = isDark ? '#121212' : '#F2F2F7';
  const hoverBg = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)';
  const defaultRowBg = isDark ? 'rgba(0,199,190,0.03)' : 'rgba(0,199,190,0.04)';

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (projectId) { selectProject(projectId); loadFlags(projectId); }
  }, [projectId, selectProject, loadFlags]);

  if (!projectId) {
    return (
      <Box sx={{ pt: 10, textAlign: 'center' }}>
        <Box sx={{ display: 'inline-flex', p: 2, bgcolor: isDark ? 'rgba(255,59,48,0.1)' : 'rgba(255,59,48,0.06)', borderRadius: 3, mb: 3 }}>
          <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>
        <Typography variant="h5" fontWeight={800} color="text.primary" mb={1}>No project selected</Typography>
        <Typography color="text.secondary">Select or create a project from the top navigation.</Typography>
      </Box>
    );
  }

  const envKeys = currentProject?.environments?.slice().sort((a, b) => a.order - b.order).map(e => e.key) ?? [];
  const defaultFlags = flags.filter(f => DEFAULT_KEYS.has(f.key));
  const customFlags = flags.filter(f => !DEFAULT_KEYS.has(f.key));
  const orderedFlags = [...defaultFlags, ...customFlags];
  const enabledCount = flags.filter(f => Object.values(f.environments).some(Boolean)).length;

  const handleEnvToggle = async (flagId: string, envKey: string, current: boolean) => {
    const flag = flags.find(f => f.flagId === flagId);
    if (!flag) return;
    await updateFlag(flagId, { environments: { ...flag.environments, [envKey]: !current } });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteFlag(deleteTarget);
    setDeleteTarget(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4, flexWrap: 'wrap', gap: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: { xs: 24, md: 30 }, letterSpacing: '-0.02em' }}>
              Feature Flags
            </Typography>
            <Chip label={currentProject?.name ?? ''} size="small" sx={{ bgcolor: isDark ? 'rgba(0,199,190,0.1)' : 'rgba(0,199,190,0.1)', color: 'secondary.main', fontWeight: 700, fontSize: 11, border: '1px solid rgba(0,199,190,0.25)' }} />
          </Box>
          <Typography sx={{ color: 'text.secondary', maxWidth: 520, lineHeight: 1.6 }}>
            Control features across environments.{' '}
            <Box component="span" sx={{ color: 'secondary.main', fontWeight: 600 }}>{enabledCount} of {flags.length}</Box>{' '}
            flags have at least one environment enabled.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<AssessmentIcon />}
            onClick={() => navigate(`/projects/${projectId}/audit`)}
            sx={{ py: 1.5, px: 2.5, fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', borderColor, color: 'text.secondary', '&:hover': { borderColor: '#0A84FF', color: '#0A84FF', bgcolor: 'rgba(10,132,255,0.06)' } }}>
            Project Audit
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => navigate(`/projects/${projectId}/flags/new`)}
            sx={{ py: 1.5, px: 3, fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap' }}>
            New Flag
          </Button>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {envKeys.map(k => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: envColor(k) }} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
              {currentProject?.environments.find(e => e.key === k)?.name ?? k}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1 }}>
          <LockIcon sx={{ fontSize: 12, color: 'secondary.main' }} />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>System default flag</Typography>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${borderColor}`, borderRadius: 4, overflow: 'hidden', bgcolor: 'background.paper' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 4, fontWeight: 700 }}>Feature Details</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Settings</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Audit</TableCell>
              {envKeys.map(k => (
                <TableCell key={k} align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: envColor(k) }} />
                    {currentProject?.environments.find(e => e.key === k)?.name ?? k}
                  </Box>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 700 }}>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedFlags.length === 0 && (
              <TableRow>
                <TableCell colSpan={4 + envKeys.length} align="center" sx={{ py: 8 }}>
                  <Typography sx={{ color: 'text.secondary', mb: 2 }}>No feature flags yet.</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/projects/${projectId}/flags/new`)} sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Create your first flag
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {orderedFlags.map((flag, idx) => {
              const isDefault = DEFAULT_KEYS.has(flag.key);
              const isLastDefault = isDefault && idx === defaultFlags.length - 1;
              return (
                <TableRow key={flag.flagId} hover sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  transition: 'background-color 0.15s',
                  bgcolor: isDefault ? defaultRowBg : 'transparent',
                  '&:hover': { bgcolor: isDefault ? (isDark ? 'rgba(0,199,190,0.06)' : 'rgba(0,199,190,0.07)') : hoverBg },
                  ...(isLastDefault && customFlags.length > 0 ? { '& td': { borderBottom: `2px solid ${isDark ? '#2C2C2E' : '#D1D1D6'} !important` } } : {}),
                }}>
                  {/* Feature details */}
                  <TableCell sx={{ pl: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isDefault && (
                          <Tooltip title="System default flag — present in every project" TransitionComponent={Zoom}>
                            <LockIcon sx={{ fontSize: 14, color: 'secondary.main', flexShrink: 0 }} />
                          </Tooltip>
                        )}
                        <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: 15 }}>{flag.name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: 12, bgcolor: keyBg, px: 1, py: 0.4, borderRadius: 1, border: `1px solid ${borderColor}` }}>
                          {flag.key}
                        </Typography>
                        {flag.dependencies.length > 0 && flag.dependencies.map(dep => (
                          <Chip key={dep} label={`depends on: ${dep}`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: 'rgba(255,159,10,0.1)', color: '#FF9F0A', border: '1px solid rgba(255,159,10,0.2)', fontFamily: 'monospace' }} />
                        ))}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Settings */}
                  <TableCell align="center">
                    <Tooltip title="Edit Feature Settings" TransitionComponent={Zoom}>
                      <IconButton onClick={() => navigate(`/projects/${projectId}/flags/${flag.flagId}`)} sx={{ color: 'text.secondary', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', '&:hover': { color: 'primary.main', bgcolor: 'rgba(255,59,48,0.1)' }, transition: 'all 0.2s', borderRadius: 2 }}>
                        <SettingsIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  {/* Audit */}
                  <TableCell align="center">
                    <Tooltip title="View audit log" TransitionComponent={Zoom}>
                      <IconButton onClick={() => navigate(`/projects/${projectId}/audit`)} sx={{ color: 'text.secondary', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', '&:hover': { color: '#0A84FF', bgcolor: 'rgba(10,132,255,0.1)' }, transition: 'all 0.2s', borderRadius: 2 }}>
                        <HistoryIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  {/* Env toggles */}
                  {envKeys.map(k => (
                    <TableCell key={k} align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Switch checked={flag.environments[k] ?? false} onChange={() => handleEnvToggle(flag.flagId, k, flag.environments[k] ?? false)} color="secondary" size="small" />
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: flag.environments[k] ? envColor(k) : 'text.secondary' }}>
                          {flag.environments[k] ? 'ON' : 'OFF'}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}

                  {/* Delete */}
                  <TableCell align="center">
                    <Tooltip title="Delete flag" TransitionComponent={Zoom}>
                      <IconButton onClick={() => setDeleteTarget(flag.flagId)} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(255,59,48,0.1)' }, borderRadius: 2 }}>
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {orderedFlags.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2.5 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            <Box component="span" sx={{ color: 'secondary.main', fontWeight: 600 }}>{defaultFlags.length}</Box> system default
            {customFlags.length > 0 && <>{' · '}<Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{customFlags.length}</Box> custom</>}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{orderedFlags.length} flag{orderedFlags.length !== 1 ? 's' : ''} total</Typography>
        </Box>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete Flag?</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ fontWeight: 700, borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
